/**
 * TypeScript types mirroring the backend /api/far-chat JSON contracts.
 * See issues/20260423-far-chatbot_spec.md §15 for the authoritative shape.
 */

/**
 * Citation source surface. `rfo` = the Revolutionary FAR Overhaul corpus,
 * `legacy` = the pre-RFO (Legacy) FAR corpus, `upload` = a user-attached
 * document. `legacy` behaves like `rfo` for rendering purposes (a
 * regulation WITH an acquisition.gov source url) — only the authority
 * label and the source page differ. See
 * issues/20260612-legacy-far-readers_spec.md §4.6.
 */
export type CitationSource = 'rfo' | 'legacy' | 'upload' | 'policy';

/**
 * v2 citation kind. Distinguishes the regulation surfaces the FAR chatbot
 * v2 cites (FAR / Legacy FAR / DFARS / PGI / DoD class deviation memo) so
 * the renderer can build kind-specific URLs and hover headers.
 * `legacy_far` renders a `Legacy FAR <section> — <title>` header and points
 * Source at the legacy acquisition.gov page (§4.6).
 * Unknown is the safe fallback for tokens that don't match any of the
 * known forms — they render with no link but with the canonical text +
 * tooltip.
 */
export type CitationKind =
  | 'far'
  | 'legacy_far'
  | 'dfars'
  | 'pgi'
  | 'memo'
  | 'policy'
  | 'unknown';

export interface Citation {
  id: number;
  source: CitationSource;
  /**
   * Citation kind (FAR / DFARS / DoD memo). Optional for backward
   * compatibility with legacy v1 citations that only had FAR.
   */
  kind?: CitationKind;
  canonical?: string;      // e.g. "FAR 6.001(a)"; present on rfo citations
  section?: string;        // e.g. "6.001"
  paragraph?: string | null; // e.g. "(a)"; legacy citations may use null
  url?: string;            // rfo only; deep-link to acquisition.gov anchor
  filename?: string;       // upload only
  page_hint?: string;      // upload only
  quote: string;
  verified: boolean;
  source_sha256?: string | null;
  source_effective_date?: string | null;
  source_verified_on?: string | null;
  source_verification?: string | null;
  /**
   * Canonical FAR section title (e.g. "Justification content") sourced
   * from the scraped Part 6 anchors. Optional / nullable for backwards
   * compatibility with legacy citations and upload citations that
   * don't have a section title.
   */
  title?: string | null;
}

export type ChatRole = 'user' | 'assistant' | 'system';

export interface ChatMessage {
  role: ChatRole;
  content: string;
  timestamp?: string;
  /**
   * Canonical durable position in a Home Chat transcript. The backend assigns
   * one sequence across legacy messages, v2 turns, and typed chat cards.
   */
  timeline_sequence?: number;
  /**
   * Legacy Home Chat position. Read only as a compatibility fallback for
   * threads persisted before `timeline_sequence` was introduced.
   */
  order?: number;
  citations?: Citation[];
  /** True when this assistant turn was a refusal (spec §15.2). */
  refusal?: boolean;
  refusal_reason?: string;
  /**
   * Structured assistant answer fields. Optional for backwards
   * compatibility — legacy assistant messages carry only `content`.
   */
  tldr?: string;
  detail?: string;
}

export interface AgentActivityStep {
  step: string;
  [key: string]: unknown;
}

export interface PendingRequest {
  client_request_id: string;
  started_at: string;
  /**
   * The user's question text for the in-flight turn. Persisted by the
   * backend on dispatch so a mid-flight reload can rehydrate the
   * `inFlightUserMsg` bubble alongside the StepProgressChecklist.
   * Optional for back-compat with legacy envelopes written before the
   * field existed (loaders treat missing as `null` / empty — checklist
   * still renders, just without the user bubble above it).
   */
  user_msg?: string | null;
}

export interface ThreadEnvelope {
  workflow_id?: string;
  messages: ChatMessage[];
  created_at: string | null;
  last_activity_at: string | null;
  message_count: number;
  /** Optional — some endpoints also return the attachments list. */
  attachments?: Attachment[];
  /**
   * Mid-flight v2 turn marker. Non-null when a turn POST is currently
   * dispatching server-side; cleared on completion / failure. The
   * frontend uses this on thread mount to rehydrate the
   * `StepProgressChecklist` after a page reload.
   */
  pending_request?: PendingRequest | null;
  /**
   * v2 chat turns persisted on disk. Optional for back-compat with
   * sessions that only have legacy v1 ``messages``. Wire shape matches
   * the POST /v2/turn response — callers normalize each entry via
   * :func:`normalizeV2Turn` before pushing into UI state.
   */
  turns?: Array<{
    turn_id: string;
    user_msg: string;
    response: {
      summary: string;
      detail: string;
      citations: Array<Citation | string>;
    };
    reader_outputs?: unknown;
    is_refusal?: boolean;
    refusal_text?: string;
    timestamp?: string;
    /** Chosen clarified question, or null/absent (legacy). See `Turn.clarification`. */
    clarification?: string | null;
    /** Chosen post-reader topic focus, or null/absent (legacy). See `Turn.topic_focus`. */
    topic_focus?: string | null;
  }>;
}

export interface Attachment {
  filename: string;
  size: number;
  mime_type: string;
}

export interface AttachmentsEnvelope {
  workflow_id?: string;
  attachments: Attachment[];
}

export interface TurnResponse {
  /** Legacy server-assembled markdown. Kept one release for fallback. */
  answer: string;
  /** Structured fields (preferred); absent on legacy backends. */
  tldr?: string;
  detail?: string;
  citations: Citation[];
  agent_activity: AgentActivityStep[];
  refusal: boolean;
  refusal_reason?: string;
  turn_id: string;
}

export interface TurnRequest {
  message: string;
  client_request_id?: string;
}

/**
 * v2 chat-turn record. A `Turn` represents one Q&A exchange (one user
 * message + one synthesizer response).
 */
export interface Turn {
  turn_id: string;
  user_msg: string;
  /** Top-3 summary text. Always-visible at the top of the bubble. */
  summary: string;
  /** Detail text. Collapsed by default in the UI. */
  detail: string;
  /** Citations referenced anywhere in summary or detail. */
  citations: Citation[];
  /** v1 refusal flag. Refusal turns render only the refusal text. */
  is_refusal?: boolean;
  /** Refusal display text when `is_refusal=true`. */
  refusal_text?: string;
  timestamp?: string;
  /** Canonical durable position in a unified Home Chat transcript. */
  timeline_sequence?: number;
  /** Legacy compatibility fallback; new server responses use timeline_sequence. */
  order?: number;
  /**
   * The chosen clarified question (the query that actually produced
   * `response`) when the FAR-chat clarification gate fired and the user
   * picked/typed a rewrite that differs from their original. `null` /
   * absent when the gate didn't fire, the user used their original
   * ("use my original"), or the turn predates the feature (legacy).
   * Drives the "Clarified: …" callout rendered below the original query
   * on reload. See issues/20260610-far-chat-clarification-step §4.1 / §5.
   */
  clarification?: string | null;
  /**
   * The user's post-reader focus selection that shaped `response` (the
   * chosen topic label or typed free text). `null` / absent when the
   * post-reader topic-focus gate didn't fire, the user skipped, the feature
   * flag was off, or the turn predates the feature (legacy). Drives the
   * "Focused on: …" callout rendered below the original query on reload.
   * See issues/20260616-far-chat-post-reader-topic-focus_spec §6.1 / §9.
   */
  topic_focus?: string | null;
  /**
   * Deterministic backend flag: `true` iff this answer cites FAR/RFO. The
   * frontend renders a fixed "not based on the FAR/RFO" notice on any
   * non-refusal answer where this is `false`. Absent / `undefined` on
   * legacy/persisted turns → treated as `true` (no banner). Computed purely
   * from citation presence/kind on the backend, never from AI text. See
   * issues/20260629-ungrounded-answer-fallback_spec-implplan §5.4.
   */
  references_far?: boolean;
  /** Server-owned marker for bounded Home Chat Training guidance. */
  training_guidance?: boolean;
  /**
   * Proactive feedback solicitation marker, lifted from the wire field
   * `feedback_request` by :func:`normalizeV2Turn`. Present (with the
   * backend-minted prompt id) only on a live answered turn the backend
   * chose to ask "How was this response?" about; `null`/absent otherwise —
   * including every history-reloaded turn. Signals the app-wide mandatory
   * feedback dialog. Outstanding prompts are recovered separately from the
   * server after refresh so historical markers cannot bypass the gate.
   */
  feedbackRequest?: { promptId: string } | null;
  /** Server-authorized only for active ordinary Home Chat v2 turns. */
  editable?: boolean;
}

/**
 * Non-answer response shape returned (HTTP 200) by ``POST /v2/turn`` when
 * the FAR-chat clarification gate decides the query is too vague to answer
 * directly. Carries ≤3 suggested rewrites plus the user's original query.
 * Discriminated against a normal answer `Turn` via the `status` field —
 * an answer never carries `status`. There is NO turn id: nothing is
 * persisted on the clarification path. See clarification-step spec §5.
 */
/**
 * The internal FAR-version selector value. NEVER rendered as the word
 * "regime" in the UI — the user-facing control is labeled "FAR Version"
 * with options "Revolutionary FAR Overhaul (RFO)" / "Legacy FAR" / "Both".
 * See issues/20260612-legacy-far-readers_spec.md §4.1.
 */
export type FarRegime = 'rfo' | 'legacy' | 'both';

/**
 * User-selected Home Chat answer path. `fast_answer` is the default fused
 * authority path, which receives the selected primary, supplement, and
 * applicable deviation package in one final-answer call. `deep_search`
 * preserves the established multi-reader analysis. The backend remains
 * authoritative for capability flags and source-selection failures.
 */
export type AnswerMode = 'fast_answer' | 'deep_search';

/**
 * Non-answer response returned when a fused-authority request cannot safely
 * complete. It is deliberately not a Turn: the client must surface an
 * explicit recovery instead of normalizing it into an empty answer shell.
 */
export interface FastAnswerRecoveryEnvelope {
  status: 'fast_answer_recovery';
  reason: string;
  original_query: string;
  regime: FarRegime;
  requested_answer_mode: 'fast_answer';
  client_request_id?: string;
  /** Present on the Home Chat session-scoped and lazy-create routes. */
  session_id?: string;
}

/** User-safe error raised by API clients for a Fast Answer recovery envelope. */
export class FastAnswerRecoveryError extends Error {
  readonly envelope: FastAnswerRecoveryEnvelope;

  constructor(envelope: FastAnswerRecoveryEnvelope) {
    super(fastAnswerRecoveryMessage(envelope.reason));
    this.name = 'FastAnswerRecoveryError';
    this.envelope = envelope;
  }
}

/** Do not expose provider/configuration detail from recovery reason codes. */
export function fastAnswerRecoveryMessage(reason: string): string {
  // Old servers may still return this envelope while a browser reloads
  // during rollout. It must never recreate an execution-path picker or a
  // blank answer shell; current servers perform any context fallback
  // internally and return a normal Turn.
  void reason;
  return 'This answer could not be completed. Please try again.';
}

export function isFastAnswerRecoveryEnvelope(
  response: unknown
): response is FastAnswerRecoveryEnvelope {
  return !!response
    && typeof response === 'object'
    && (response as { status?: unknown }).status === 'fast_answer_recovery';
}

export interface ClarificationEnvelope {
  status: 'clarification_needed';
  /**
   * ≤3 suggested rewrites. MAY be empty (the clear-query, selector-only
   * mode): the clarification step now always surfaces for relevant queries
   * so the user can confirm the FAR Version even when no rewrite is
   * proposed (§4.1).
   */
  enhancements: string[];
  /** The user's original query text. */
  original_query: string;
  /**
   * The agent's FAR-version pre-selection (internal `regime`). Pre-fills
   * the "FAR Version" selector in the clarification card; the user's choice
   * is authoritative. Optional/defaulted to `'rfo'` for back-compat with
   * envelopes written before this field existed (§4.1).
   */
  regime?: FarRegime;
  /**
   * Server-authoritative modes available for this clarification handoff.
   * Missing preserves compatibility with servers released before the Fast
   * Answer capability field, which exposed both choices.
   */
  available_answer_modes?: AnswerMode[];
  /** Echoed back when supplied on the request. */
  client_request_id?: string;
  /**
   * Only present on the HomeChat lazy-create path
   * (``POST /sessions/new/v2/turn``) so the client can resubmit the
   * resolved query to the freshly-minted session.
   */
  session_id?: string;
}

/**
 * Type guard discriminating the `clarification_needed` envelope from a
 * normal answer `Turn` (which never carries a `status` field).
 */
export function isClarificationEnvelope(
  resp: Turn | ClarificationEnvelope | TopicClarificationEnvelope
): resp is ClarificationEnvelope {
  return (
    !!resp &&
    typeof (resp as ClarificationEnvelope).status === 'string' &&
    (resp as ClarificationEnvelope).status === 'clarification_needed'
  );
}

/**
 * Non-answer response shape returned (HTTP 200) by `POST /v2/turn` when the
 * POST-reader topic-focus gate decides the readers' answers span multiple
 * materially distinct topics. Carries the clarifying `question`, the candidate
 * focus `topics` (single-select), the user's original query, and an opaque
 * `focus_token` keying the server-held reader outputs. The resolving resubmit
 * sends `focus_token` + the chosen `topic_focus` (a topic, typed free text, or
 * `""` for Skip). Discriminated against a normal answer `Turn` and against the
 * pre-reader `ClarificationEnvelope` via the `status` field.
 * See issues/20260616-far-chat-post-reader-topic-focus_spec §6.3 / §9.
 */
export interface TopicClarificationEnvelope {
  status: 'topic_clarification_needed';
  /** The short clarifying question shown above the topic options. */
  question: string;
  /** Candidate focus topics the readers discussed (single-select options). */
  topics: string[];
  /** The user's original query text. */
  original_query: string;
  /** Opaque key into the server-side pending-focus store (the held reader outputs). */
  focus_token: string;
  /**
   * The FAR-version (internal `regime`) the readers ran under. Optional /
   * defaulted to `'rfo'` for back-compat with envelopes written before this
   * field existed.
   */
  regime?: FarRegime;
  /**
   * The pre-reader clarified question (the held `final_question`) when this
   * topic-focus turn followed a pre-reader clarification gate — i.e. the
   * readers ran on the user's CLARIFIED question, not their original. `null`
   * / absent when there was no pre-reader clarification (the readers ran on
   * the original query) or for envelopes written before this field existed.
   * Threaded through so the "Clarified: …" callout stays visible continuously
   * from the topic-card phase through the focus resubmit, and so the focus
   * resubmit uses it as the cache-miss `final_question` fallback (the readers
   * ran on it). See Bug 1 in the focus-resubmit clarification-continuity fix.
   */
  clarification?: string | null;
  /** Echoed back when supplied on the request. */
  client_request_id?: string;
  /**
   * Only present on the HomeChat lazy-create path so the client can resubmit
   * the focus choice to the freshly-minted session.
   */
  session_id?: string;
}

/**
 * Type guard discriminating the `topic_clarification_needed` envelope from a
 * normal answer `Turn` and from the pre-reader `clarification_needed` envelope.
 */
export function isTopicClarificationEnvelope(
  resp: Turn | ClarificationEnvelope | TopicClarificationEnvelope
): resp is TopicClarificationEnvelope {
  return (
    !!resp &&
    typeof (resp as TopicClarificationEnvelope).status === 'string' &&
    (resp as TopicClarificationEnvelope).status === 'topic_clarification_needed'
  );
}

/** Thrown on non-2xx responses from the FarChat backend. */
export class FarChatApiError extends Error {
  status: number;
  body?: unknown;
  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'FarChatApiError';
    this.status = status;
    this.body = body;
  }
}
